import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const COLORS = ["#DC2626", "#1D4ED8", "#059669", "#F59E0B"];

const Board = ({ dev, pins, board, value, usedBy }) => {
  const top = 16, gap = 21, H = top * 2 + gap * (board.length - 1);
  const my = (i) => H / 2 - ((pins.length - 1) * 46) / 2 + i * 46;
  const by = (j) => top + j * gap;
  return (
    <svg viewBox={`0 0 420 ${H}`} className="w-full rounded-2xl bg-[#0B1F3A]" data-testid="lab-wire-board">
      <rect x="14" y={my(0) - 28} width="120" height={(pins.length - 1) * 46 + 56} rx="12" fill="#134E4A" />
      <text x="74" y={my(0) - 34} fill="#99F6E4" fontSize="10" fontWeight="700" textAnchor="middle">{dev}</text>
      <rect x="270" y="4" width="136" height={H - 8} rx="12" fill="#1E3A8A" />
      <text x="392" y={H / 2} fill="#BFDBFE" fontSize="12" fontWeight="800" textAnchor="middle" transform={`rotate(90 392 ${H / 2})`}>ESP32 DevKit</text>
      {board.map((b, j) => (
        <g key={b}>
          <circle cx="270" cy={by(j)} r="5" fill={usedBy[b] ? "#64748B" : b === "GND" ? "#94A3B8" : b.startsWith("GPIO") ? "#E2E8F0" : "#FCA5A5"} />
          <text x="280" y={by(j) + 4} fill={usedBy[b] ? "#94A3B8" : "#E2E8F0"} fontSize="10" fontWeight="600">{b}{usedBy[b] ? ` · ${usedBy[b]}` : ""}</text>
        </g>
      ))}
      {pins.map((p, i) => <g key={p}><circle cx="134" cy={my(i)} r="6" fill={COLORS[i % 4]} /><text x="124" y={my(i) + 4} fill="#fff" fontSize="11" textAnchor="end" fontWeight="700">{p}</text></g>)}
      {pins.map((p, i) => {
        const j = board.indexOf(value[p]);
        return j < 0 ? null : <path key={p} d={`M134 ${my(i)} C 205 ${my(i)}, 200 ${by(j)}, 270 ${by(j)}`} stroke={COLORS[i % 4]} strokeWidth="3.5" fill="none" strokeLinecap="round" />;
      })}
    </svg>
  );
};

export const WirePanel = ({ lab, wiring, setWiring, bad }) => {
  const { L, lang } = useLang();
  const ids = Object.keys(lab.devices);
  const [sel, setSel] = useState(ids[0]);
  const dev = lab.devices[sel];
  const nm = (d) => (lang === "hi" ? lab.devices[d].hi : lab.devices[d].en);
  const v = wiring[sel] || {};
  const usedBy = {};
  ids.forEach((d) => d !== sel && Object.values(wiring[d] || {}).forEach((b) => { if (b.startsWith("GPIO")) usedBy[b] = nm(d).split(" ")[0]; }));
  const complete = (d) => lab.devices[d].pins.every((p) => (wiring[d] || {})[p]);
  return (
    <div data-testid="lab-wire-panel">
      <div className="flex flex-wrap gap-1.5">
        {ids.map((d) => (
          <button key={d} type="button" onClick={() => setSel(d)} className={`rounded-full px-3 h-8 text-xs font-bold inline-flex items-center gap-1 ${sel === d ? "bg-[#0B1F3A] text-white" : bad?.[d]?.length ? "bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]" : "bg-white border border-slate-200 text-[#0B1F3A]"}`} data-testid={`lab-wire-tab-${d}`}>
            {complete(d) && <CheckCircle2 className="w-3.5 h-3.5 text-[#14B8A6]" />}{nm(d)}
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-600 rounded-xl bg-[#F0FDFA] border border-[#99F6E4] p-2.5" data-testid="lab-wire-hint">{lang === "hi" ? dev.hint_hi : dev.hint}</p>
      <div className="mt-3"><Board dev={nm(sel)} pins={dev.pins} board={lab.board} value={v} usedBy={usedBy} /></div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {dev.pins.map((p, i) => (
          <label key={p} className={`rounded-xl border px-3 py-2 text-sm bg-white ${bad?.[sel]?.includes(p) ? "border-[#F87171]" : "border-slate-200"}`}>
            <span className="font-bold" style={{ color: COLORS[i % 4] }}>{p}</span> →
            <select value={v[p] || ""} onChange={(e) => setWiring({ ...wiring, [sel]: { ...v, [p]: e.target.value } })} className="ml-1 rounded-lg border border-slate-300 px-1.5 py-1 text-sm" data-testid={`lab-wire-${sel}-${p}`}>
              <option value="">{L("pin", "पिन")}</option>
              {lab.board.map((b) => <option key={b} value={b}>{b}{usedBy[b] ? ` (${usedBy[b]})` : ""}</option>)}
            </select>
          </label>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">{L("Each signal needs its own GPIO. GPIO34 and GPIO35 are input-only. Analog signals need an ADC1 pin (GPIO32 to GPIO35).", "हर सिग्नल को अपना GPIO चाहिए। GPIO34 और GPIO35 सिर्फ़ इनपुट हैं। एनालॉग सिग्नल को ADC1 पिन (GPIO32 से GPIO35) चाहिए।")}</p>
    </div>
  );
};
